import { useEffect, useRef, useState } from "react";
import { Download, RefreshCw, WifiOff, X } from "lucide-react";
import { useLocation } from "wouter";
import { adminPath, isAdminPath } from "@/lib/admin-route";
import {
  adminInstallDismissalKey,
  adminInstalledKey,
  hasSuppressedAdminInstall,
  rememberAdminInstallShown,
  rememberAdminInstalled,
  shouldShowAdminInstall,
} from "@/lib/pwa-install-policy";

type BeforeInstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
};

type BadgeNavigator = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

function appBasePath() {
  return import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
}

function isIosDevice() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isInstalledAsApp() {
  return window.matchMedia("(display-mode: standalone)").matches
    || ("standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
}

function setLink(rel: string, href: string, sizes?: string) {
  let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.append(element);
  }
  element.href = href;
  if (sizes) element.sizes = sizes;
}

function setMeta(name: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.append(element);
  }
  element.content = content;
}

export function useAdminPushUnreadCount(enabled = true) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled || !("serviceWorker" in navigator)) {
      setCount(0);
      return;
    }

    const base = appBasePath();
    const adminAppPath = `${base}${adminPath().replace(/^\/+/, "")}`.replace(/\/+$/, "");
    const scope = `${adminAppPath}/`;
    const script = `${scope}sw.js`;
    let disposed = false;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "ADMIN_PUSH_UNREAD_COUNT" && typeof event.data.count === "number") {
        setCount(Math.max(0, Math.floor(event.data.count)));
      }
    };
    const requestCount = async () => {
      try {
        let registration = await navigator.serviceWorker.getRegistration(scope);
        if (!registration) {
          registration = await navigator.serviceWorker.register(script, { scope });
        }
        if (disposed) return;
        const worker = registration.active ?? registration.waiting ?? registration.installing;
        const adminAppPath = `${base}${adminPath().replace(/^\/+/, "")}`.replace(/\/+$/, "");
        worker?.postMessage({ type: "SET_ADMIN_PATH", path: adminAppPath });
        worker?.postMessage({ type: "GET_UNREAD_COUNT" });
      } catch {
        // The page remains usable if service workers or app badging are unavailable.
      }
    };
    const onFocus = () => void requestCount();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void requestCount();
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    void requestCount();

    return () => {
      disposed = true;
      navigator.serviceWorker.removeEventListener("message", onMessage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled]);

  return count;
}

export default function PwaExperience() {
  const [location] = useLocation();
  const isAdminRoute = isAdminPath(location);
  const unreadCount = useAdminPushUnreadCount(isAdminRoute);
  const waitingWorker = useRef<ServiceWorker | null>(null);
  const reloadAfterUpdate = useRef(false);
  const noticeStack = useRef<HTMLDivElement>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPrompt | null>(null);
  const [installed, setInstalled] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(true);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const element = noticeStack.current;
    if (!element) return;
    const reserveSpace = () => {
      const stackHeight = Math.ceil(element.getBoundingClientRect().height);
      const bottomOffset = Number.parseFloat(window.getComputedStyle(element).bottom) || 0;
      document.documentElement.style.setProperty("--pwa-notice-space", `${stackHeight + bottomOffset + 12}px`);
    };
    reserveSpace();
    const observer = new ResizeObserver(reserveSpace);
    observer.observe(element);
    return () => { observer.disconnect(); document.documentElement.style.removeProperty("--pwa-notice-space"); };
  }, []);

  useEffect(() => {
    const base = appBasePath();
    const adminAppPath = `${base}${adminPath().replace(/^\/+/, "")}`.replace(/\/+$/, "");
    const manifestUrl = isAdminRoute
      ? `${adminAppPath}/manifest.webmanifest`
      : `${base}manifest.webmanifest`;
    const iconBase = isAdminRoute ? `${base}admin/` : base;
    setLink("manifest", manifestUrl);
    setLink("apple-touch-icon", `${iconBase}icons/${isAdminRoute ? "admin-180.png" : "public-180.png"}`, "180x180");
    setMeta("theme-color", isAdminRoute ? "#352622" : "#49352f");
    setMeta("apple-mobile-web-app-capable", "yes");
    setMeta("apple-mobile-web-app-title", isAdminRoute ? "CP Admin" : "ChamakPatti");
    setMeta("apple-mobile-web-app-status-bar-style", "default");
    const runningAsApp = isInstalledAsApp();
    if (isAdminRoute && runningAsApp) rememberAdminInstalled(window.localStorage);
    setInstalled(runningAsApp || (isAdminRoute && window.localStorage.getItem(adminInstalledKey) === "1"));
    setInstallDismissed(!isAdminRoute || hasSuppressedAdminInstall(window.localStorage));
    setDeferredPrompt(null);
    setIosHelpOpen(false);

    const updateManifestInstallPrompt = (event: Event) => {
      event.preventDefault();
      // Keep browser-menu installation available, but never advertise installation on the shop.
      if (isAdminRoute && !isInstalledAsApp() && !hasSuppressedAdminInstall(window.localStorage)) {
        setDeferredPrompt(event as BeforeInstallPrompt);
      }
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setInstallDismissed(true);
      if (isAdminRoute) rememberAdminInstalled(window.localStorage);
    };
    const displayMode = window.matchMedia("(display-mode: standalone)");
    const onDisplayModeChange = () => {
      if (isInstalledAsApp()) onAppInstalled();
    };
    const onStorage = (event: StorageEvent) => {
      if (isAdminRoute && event.key === adminInstalledKey && event.newValue === "1") {
        onAppInstalled();
      }
    };

    window.addEventListener("beforeinstallprompt", updateManifestInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    displayMode.addEventListener("change", onDisplayModeChange);
    window.addEventListener("storage", onStorage);

    let registration: ServiceWorkerRegistration | null = null;
    let hadActiveWorker = false;
    const onControllerChange = () => {
      if (reloadAfterUpdate.current) window.location.reload();
    };
    const exposeWaitingUpdate = () => {
      if (hadActiveWorker && registration?.waiting) {
        waitingWorker.current = registration.waiting;
        setUpdateReady(true);
      }
    };
    const onUpdateFound = () => {
      const installing = registration?.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed") exposeWaitingUpdate();
      });
    };

    if ("serviceWorker" in navigator && window.isSecureContext) {
      const adminAppPath = `${base}${adminPath().replace(/^\/+/, "")}`.replace(/\/+$/, "");
      const scope = isAdminRoute ? `${adminAppPath}/` : base;
      const script = `${scope}sw.js`;
      void (async () => {
        try {
          registration = await navigator.serviceWorker.getRegistration(scope) ?? null;
          hadActiveWorker = Boolean(registration?.active);
          registration = await navigator.serviceWorker.register(script, { scope });
          navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
          registration.addEventListener("updatefound", onUpdateFound);
          exposeWaitingUpdate();
          await registration.update().catch(() => undefined);
          exposeWaitingUpdate();
        } catch {
          // Keep the storefront usable if the browser blocks service workers.
        }
      })();
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", updateManifestInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      displayMode.removeEventListener("change", onDisplayModeChange);
      window.removeEventListener("storage", onStorage);
      navigator.serviceWorker?.removeEventListener("controllerchange", onControllerChange);
      registration?.removeEventListener("updatefound", onUpdateFound);
    };
  }, [isAdminRoute]);

  useEffect(() => {
    const setNetworkState = () => setOffline(!navigator.onLine);
    setNetworkState();
    window.addEventListener("online", setNetworkState);
    window.addEventListener("offline", setNetworkState);
    return () => {
      window.removeEventListener("online", setNetworkState);
      window.removeEventListener("offline", setNetworkState);
    };
  }, []);

  useEffect(() => {
    if (!isAdminRoute) return;
    const badgeNavigator = navigator as BadgeNavigator;
    if (unreadCount > 0) {
      void badgeNavigator.setAppBadge?.(unreadCount).catch(() => undefined);
    } else {
      void badgeNavigator.clearAppBadge?.().catch(() => undefined);
    }
  }, [isAdminRoute, unreadCount]);

  async function installApp() {
    if (!deferredPrompt) {
      setIosHelpOpen(true);
      return;
    }
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismissInstallPrompt();
    if (choice.outcome === "accepted") {
      setInstalled(true);
    }
  }

  function dismissInstallPrompt() {
    window.localStorage.setItem(adminInstallDismissalKey, "1");
    setInstallDismissed(true);
  }

  function activateUpdate() {
    if (!waitingWorker.current) return;
    reloadAfterUpdate.current = true;
    waitingWorker.current.postMessage({ type: "SKIP_WAITING" });
  }

  const shouldShowInstall = shouldShowAdminInstall({
    admin: isAdminRoute,
    installed,
    suppressed: installDismissed,
    nativePrompt: Boolean(deferredPrompt),
    ios: isIosDevice(),
  });
  useEffect(() => {
    // Remember the first appearance without closing the currently visible offer.
    if (shouldShowInstall) rememberAdminInstallShown(window.localStorage);
  }, [shouldShowInstall]);
  const appName = isAdminRoute ? "admin control room" : "ChamakPatti";

  return (
    <div ref={noticeStack} data-pwa-notice-stack className="pointer-events-none fixed inset-x-0 bottom-4 z-[120] mx-auto flex max-w-3xl flex-col gap-2 px-3 sm:bottom-6 sm:px-5" aria-live="polite">
      {!isAdminRoute && offline && (
        <div className="pointer-events-auto flex items-start gap-3 border border-[#d8c8b9] bg-[#f5e9dc] p-3 text-sm text-[#704e3e] shadow-lg" role="status" data-testid="status-pwa-offline">
          <WifiOff size={17} className="mt-0.5 shrink-0" />
          <p className="flex-1 leading-5">You’re offline. Previously opened shop pages may still be available on this device.</p>
        </div>
      )}

      {updateReady && (
        <div className="pointer-events-auto flex flex-wrap items-center justify-between gap-3 border border-[#d5d0c6] bg-[#fffdf8] p-3 text-sm text-[#302927] shadow-lg" role="status" data-testid="status-pwa-update">
          <span>A new {appName} update is ready.</span>
          <button type="button" onClick={activateUpdate} className="solid-button min-h-10 px-4 text-[10px] tracking-[.12em]" data-testid="button-pwa-update">
            <RefreshCw size={14} /> RELOAD TO UPDATE
          </button>
        </div>
      )}

      {shouldShowInstall && (
        <div className="pointer-events-auto border border-[#d5d0c6] bg-[#fffdf8] p-4 shadow-lg sm:p-5" role="region" aria-label={`Install ${appName}`} data-testid="panel-pwa-install">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#eee4da] text-[#71483e]">
              <Download size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="mono text-[9px] tracking-[.18em] text-[#815348]">ADD TO YOUR HOME SCREEN</p>
              <h2 className="serif mt-1 text-xl">Install the admin app.</h2>
              <p className="mt-1 text-xs leading-5 text-[#77736d]">
                Open the separate control room from your device. Sign-in remains required.
              </p>
              {iosHelpOpen && (
                <p className="mt-3 border-l-2 border-[#c99579] pl-3 text-xs leading-5 text-[#653f34]" data-testid="text-pwa-ios-help">
                  In Safari, tap Share, then choose “Add to Home Screen”.
                </p>
              )}
            </div>
            <button type="button" onClick={dismissInstallPrompt} className="flex h-9 w-9 shrink-0 items-center justify-center text-[#77736d] hover:bg-[#f3f0e9]" aria-label="Dismiss install prompt" data-testid="button-pwa-install-dismiss">
              <X size={16} />
            </button>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => void installApp()} className="solid-button min-h-10 px-4 text-[10px] tracking-[.12em]" data-testid="button-pwa-install">
              {isIosDevice() && !deferredPrompt ? "HOW TO INSTALL" : "INSTALL APP"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
